import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiError, fileUrl } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { PageHeader, Card, Modal, Field, inputClass, PrimaryButton, EmptyState } from "../components/common";
import { toast } from "sonner";
import { Table, Plus, Trash, ArrowClockwise, DownloadSimple, ArrowLeft, FileXls } from "@phosphor-icons/react";

const money = (n) => "$" + (Number(n) || 0).toLocaleString("es-MX", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const PERIODS = [{ v: "mensual", l: "Mensual" }, { v: "semanal", l: "Semanal" }, { v: "diario", l: "Diario" }];

const periodKey = (d, period) => {
  const s = (d || "").slice(0, 10);
  const [y, m, day] = s.split("-");
  if (!y || !m) return "—";
  if (period === "diario") return s;
  if (period === "semanal") {
    const dt = new Date(Number(y), Number(m) - 1, Number(day || 1));
    const oneJan = new Date(dt.getFullYear(), 0, 1);
    const week = Math.ceil(((dt - oneJan) / 86400000 + oneJan.getDay() + 1) / 7);
    return `${y}-Sem${String(week).padStart(2, "0")}`;
  }
  return `${y}-${m}`;
};

export default function ErpReports() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const canDelete = ["admin", "gerente_general"].includes(user?.role) || !!user?.can_delete;
  const [reports, setReports] = useState([]);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ name: "" });
  const [file, setFile] = useState(null);
  const [selected, setSelected] = useState(null);
  const [detail, setDetail] = useState(null);
  const [period, setPeriod] = useState("mensual");
  const [supQuery, setSupQuery] = useState("");
  const [supResult, setSupResult] = useState(null);
  const [supBusy, setSupBusy] = useState(false);
  const [recon, setRecon] = useState(null);
  const [reconBusy, setReconBusy] = useState(false);
  const [globalQuery, setGlobalQuery] = useState("");
  const [globalResult, setGlobalResult] = useState(null);
  const [globalBusy, setGlobalBusy] = useState(false);
  const globalSearch = async () => {
    if (!globalQuery.trim()) return toast.error("Escribe qué quieres buscar");
    setGlobalBusy(true);
    try { const r = await api.post("/erp/search", { query: globalQuery }); setGlobalResult(r.data); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setGlobalBusy(false); }
  };
  const globalExcel = async () => {
    try {
      const r = await api.get("/erp/search-excel", { params: { query: globalResult.query }, responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a"); a.href = url; a.download = `erp_${globalResult.query}.xlsx`;
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
    } catch (err) { toast.error("No se pudo exportar el Excel"); }
  };
  const [sumGroupBy, setSumGroupBy] = useState("proveedor");
  const [sumStart, setSumStart] = useState("");
  const [sumEnd, setSumEnd] = useState("");
  const [sumQ, setSumQ] = useState("");
  const [sumData, setSumData] = useState(null);
  const [sumBusy, setSumBusy] = useState(false);
  const loadSummary = async () => {
    setSumBusy(true);
    try { const r = await api.get("/erp/summary", { params: { group_by: sumGroupBy, start: sumStart || undefined, end: sumEnd || undefined, q: sumQ || undefined } }); setSumData(r.data); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setSumBusy(false); }
  };
  const loadReconciliation = async () => {
    setReconBusy(true);
    try { const r = await api.get(`/erp/reports/${detail.id}/reconciliation`); setRecon(r.data); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setReconBusy(false); }
  };
  const downloadReconExcel = async () => {
    try {
      const r = await api.get(`/erp/reports/${detail.id}/reconciliation-excel`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a"); a.href = url; a.download = "conciliacion_erp.xlsx";
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
    } catch (err) { toast.error("No se pudo exportar el Excel"); }
  };
  const registerSupplier = (name) => {
    navigate(`/proveedores?nuevo=${encodeURIComponent(name)}`);
  };
  const searchSupplier = async () => {
    if (!supQuery.trim()) return toast.error("Escribe el nombre del proveedor");
    setSupBusy(true);
    try { const r = await api.post(`/erp/reports/${detail.id}/supplier-summary`, { query: supQuery }); setSupResult(r.data); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setSupBusy(false); }
  };
  const downloadSupplierExcel = async () => {
    try {
      const r = await api.get(`/erp/reports/${detail.id}/supplier-excel`, { params: { query: supResult.query }, responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a"); a.href = url; a.download = `pagos_${supResult.query}.xlsx`;
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
    } catch (err) { toast.error("No se pudo exportar el Excel"); }
  };

  const load = () => api.get("/erp/reports").then((r) => setReports(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  useEffect(() => {
    if (!reports.some((r) => r.status === "procesando")) return;
    const t = setInterval(load, 5000);
    return () => clearInterval(t);
  }, [reports]);

  const openDetail = (rep) => {
    setSelected(rep); setDetail(null); setSupQuery(""); setSupResult(null); setRecon(null);
    api.get(`/erp/reports/${rep.id}`).then((r) => setDetail(r.data)).catch(() => toast.error("No se pudo abrir el reporte"));
  };

  const save = async (e) => {
    e.preventDefault();
    if (!file) return toast.error("Selecciona el archivo del reporte (Excel, PDF o CSV)");
    setBusy(true);
    const fd = new FormData(); fd.append("name", form.name); fd.append("file", file);
    try {
      await api.post("/erp/reports", fd);
      toast.success("Reporte subido — la IA lo está digiriendo…");
      setOpen(false); setForm({ name: "" }); setFile(null); load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const remove = async (id) => {
    if (!window.confirm("¿Eliminar reporte?")) return;
    try { await api.delete(`/erp/reports/${id}`); setSelected(null); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const downloadExcel = (id) => {
    const url = `${api.defaults.baseURL}/erp/reports/${id}/excel?period=${period}`;
    window.open(url, "_blank");
  };

  // Detail grouping
  const groups = {};
  (detail?.rows || []).forEach((r) => {
    const k = periodKey(r.date, period);
    if (!groups[k]) groups[k] = { count: 0, total: 0, rows: [] };
    groups[k].count += 1; groups[k].total += r.amount || 0; groups[k].rows.push(r);
  });
  const groupKeys = Object.keys(groups).sort();

  if (selected && detail) {
    return (
      <div>
        <button onClick={() => setSelected(null)} data-testid="erp-back" className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-primary mb-3"><ArrowLeft size={16} /> Volver a reportes</button>
        <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
          <div>
            <div className="overline">Reporte de pagos ERP</div>
            <h1 className="font-heading text-2xl font-bold">{detail.name}</h1>
            <div className="text-sm text-muted-foreground mt-1">{detail.row_count} movimientos · Total {money(detail.total)} {detail.currency}</div>
          </div>
          <div className="flex items-center gap-2">
            {detail.file_path && <a href={fileUrl(detail.file_path)} target="_blank" rel="noreferrer" data-testid="erp-download-original" className="inline-flex items-center gap-2 h-11 px-4 border border-input rounded-sm font-medium text-sm hover:bg-muted transition-colors"><FileXls size={18} weight="fill" /> Descargar archivo original</a>}
            <select data-testid="erp-period" value={period} onChange={(e) => setPeriod(e.target.value)} className="h-11 px-3 rounded-sm border border-input bg-white text-sm">
              {PERIODS.map((p) => <option key={p.v} value={p.v}>{p.l}</option>)}
            </select>
            <PrimaryButton onClick={() => downloadExcel(detail.id)} data-testid="erp-download-excel"><FileXls size={18} weight="fill" /> Descargar Excel</PrimaryButton>
          </div>
        </div>

        {detail.status !== "error" && (detail.rows || []).length > 0 && (
          <Card className="p-5 mb-6" data-testid="erp-supplier-search">
            <div className="overline mb-1">Buscar pagos de un proveedor (IA)</div>
            <p className="text-sm text-muted-foreground mb-3">La IA agrupa las variantes del nombre (razón social, abreviaturas) y extrae montos, fechas y todos los pagos de ese proveedor.</p>
            <div className="flex flex-wrap gap-2">
              <input data-testid="erp-supplier-query" className={inputClass + " flex-1 min-w-[220px]"} placeholder="Ej. Aceros del Norte, CEMEX…" value={supQuery} onChange={(e) => setSupQuery(e.target.value)} onKeyDown={(e) => e.key === "Enter" && searchSupplier()} />
              <PrimaryButton onClick={searchSupplier} disabled={supBusy} data-testid="erp-supplier-search-btn">{supBusy ? "Buscando…" : "Buscar con IA"}</PrimaryButton>
            </div>
            {supResult && (
              <div className="mt-4" data-testid="erp-supplier-result">
                {supResult.count === 0 ? (
                  <div className="text-sm text-muted-foreground">No se encontraron pagos para “{supResult.query}”.</div>
                ) : (
                  <>
                    <div className="flex flex-wrap gap-4 text-sm mb-3 items-center">
                      <span><b className="text-primary text-lg">{money(supResult.total)}</b> total</span>
                      <span className="text-muted-foreground">{supResult.count} pago(s)</span>
                      <span className="text-muted-foreground">{supResult.first_date} → {supResult.last_date}</span>
                      <button onClick={downloadSupplierExcel} data-testid="erp-supplier-excel" className="ml-auto inline-flex items-center gap-1 text-xs font-semibold text-primary hover:underline"><FileXls size={14} weight="fill" /> Exportar Excel</button>
                    </div>
                    {supResult.matched_names?.length > 0 && <div className="text-xs text-muted-foreground mb-2">Coincidencias: {supResult.matched_names.join(", ")}</div>}
                    <div className="overflow-x-auto border border-border rounded-sm">
                      <table className="w-full text-sm">
                        <thead><tr className="border-b border-border text-left">{["Fecha", "Beneficiario", "Concepto", "Referencia", "Importe"].map((h) => <th key={h} className="px-3 py-2 overline">{h}</th>)}</tr></thead>
                        <tbody>
                          {supResult.payments.map((r, i) => (
                            <tr key={i} className="border-b border-border last:border-0">
                              <td className="px-3 py-2 whitespace-nowrap">{r.date}</td>
                              <td className="px-3 py-2">{r.beneficiary || "—"}</td>
                              <td className="px-3 py-2">{r.description || "—"}</td>
                              <td className="px-3 py-2 text-muted-foreground">{r.reference || "—"}</td>
                              <td className="px-3 py-2 font-semibold text-right whitespace-nowrap">{money(r.amount)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </>
                )}
              </div>
            )}
          </Card>
        )}

        {detail.status !== "error" && (detail.rows || []).length > 0 && (
          <Card className="p-5 mb-6" data-testid="erp-reconciliation">
            <div className="flex items-center justify-between gap-3 mb-1">
              <div className="overline">Conciliación ERP ↔ Directorio</div>
              <div className="flex items-center gap-2">
                {recon && <button onClick={downloadReconExcel} data-testid="erp-recon-excel" className="inline-flex items-center gap-1 text-xs font-semibold text-primary hover:underline whitespace-nowrap"><FileXls size={14} weight="fill" /> Exportar Excel</button>}
                <PrimaryButton onClick={loadReconciliation} disabled={reconBusy} data-testid="erp-recon-btn">{reconBusy ? "Analizando…" : (recon ? "Actualizar" : "Conciliar pagos")}</PrimaryButton>
              </div>
            </div>
            <p className="text-sm text-muted-foreground mb-3">Cruza los pagos del ERP con tus contratistas/proveedores: compara lo contratado vs lo pagado y detecta pagos a beneficiarios que NO están en tu directorio.</p>
            {recon && (
              <div className="space-y-5" data-testid="erp-recon-result">
                <div>
                  <div className="text-sm font-semibold mb-2">Conciliados ({recon.matched_count})</div>
                  <div className="overflow-x-auto border border-border rounded-sm">
                    <table className="w-full text-sm">
                      <thead><tr className="border-b border-border text-left">{["Beneficiario", "Tipo", "Contratado", "Pagado (ERP)", "Diferencia"].map((h) => <th key={h} className="px-3 py-2 overline">{h}</th>)}</tr></thead>
                      <tbody>
                        {recon.reconciled.length === 0 ? <tr><td colSpan={5} className="px-3 py-4 text-center text-muted-foreground">Sin coincidencias con el directorio.</td></tr> : recon.reconciled.map((r, i) => (
                          <tr key={i} className="border-b border-border last:border-0">
                            <td className="px-3 py-2">{r.beneficiary}<div className="text-xs text-muted-foreground">→ {r.matched_name}</div></td>
                            <td className="px-3 py-2 capitalize">{r.matched_type}</td>
                            <td className="px-3 py-2">{r.contracted != null ? money(r.contracted) : "—"}</td>
                            <td className="px-3 py-2 font-semibold">{money(r.erp_paid)}</td>
                            <td className={`px-3 py-2 font-semibold ${r.diff > 0 ? "text-amber-600" : r.diff < 0 ? "text-red-600" : "text-emerald-600"}`}>{r.diff != null ? money(r.diff) : "—"}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
                <div>
                  <div className="text-sm font-semibold mb-2 text-red-600">Pagos NO registrados en tu directorio ({recon.unmatched_count}{recon.unmatched_count > 0 ? ` · ${money(recon.total_unmatched)}` : ""})</div>
                  <div className="overflow-x-auto border border-border rounded-sm">
                    <table className="w-full text-sm">
                      <thead><tr className="border-b border-border text-left">{["Beneficiario", "# Pagos", "Pagado (ERP)", ""].map((h, i) => <th key={i} className="px-3 py-2 overline">{h}</th>)}</tr></thead>
                      <tbody>
                        {recon.unmatched.length === 0 ? <tr><td colSpan={4} className="px-3 py-4 text-center text-emerald-600">Todos los pagos están registrados.</td></tr> : recon.unmatched.map((r, i) => (
                          <tr key={i} className="border-b border-border last:border-0">
                            <td className="px-3 py-2">{r.beneficiary}</td>
                            <td className="px-3 py-2">{r.count}</td>
                            <td className="px-3 py-2 font-semibold">{money(r.erp_paid)}</td>
                            <td className="px-3 py-2 text-right"><button onClick={() => registerSupplier(r.beneficiary)} data-testid={`recon-register-${i}`} className="inline-flex items-center gap-1 text-xs font-semibold text-primary hover:underline whitespace-nowrap"><Plus size={13} weight="bold" /> Dar de alta</button></td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            )}
          </Card>
        )}

        {detail.status === "error" ? (
          <Card className="p-6 text-danger">{detail.error || "No se pudo procesar el reporte."}</Card>
        ) : groupKeys.length === 0 ? (
          <EmptyState icon={Table} title="Sin movimientos detectados" subtitle="La IA no encontró filas de pago en el archivo." />
        ) : (
          <div className="space-y-6">
            {groupKeys.map((k) => (
              <div key={k} data-testid={`erp-period-${k}`}>
                <div className="flex items-center justify-between mb-2">
                  <h3 className="font-heading font-bold text-lg">{k}</h3>
                  <div className="text-sm"><span className="text-muted-foreground">{groups[k].count} mov. · </span><span className="font-bold text-primary">{money(groups[k].total)}</span></div>
                </div>
                <Card className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead><tr className="border-b border-border text-left">{["Fecha", "Beneficiario", "Concepto", "Referencia", "Tipo", "Importe"].map((h) => <th key={h} className="px-4 py-2.5 overline">{h}</th>)}</tr></thead>
                    <tbody>
                      {groups[k].rows.map((r, i) => (
                        <tr key={i} className="border-b border-border last:border-0">
                          <td className="px-4 py-2.5 whitespace-nowrap">{r.date}</td>
                          <td className="px-4 py-2.5">{r.beneficiary || "—"}</td>
                          <td className="px-4 py-2.5">{r.description || "—"}</td>
                          <td className="px-4 py-2.5 text-muted-foreground">{r.reference || "—"}</td>
                          <td className="px-4 py-2.5 capitalize">{r.type || "—"}</td>
                          <td className="px-4 py-2.5 font-semibold text-right whitespace-nowrap">{money(r.amount)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </Card>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  }

  return (
    <div>
      <PageHeader overline="Tesorería" title="Reporte de pagos (ERP)"
        action={<PrimaryButton onClick={() => { setForm({ name: "" }); setFile(null); setOpen(true); }} data-testid="erp-upload-btn"><Plus size={18} weight="bold" /> Subir reporte</PrimaryButton>} />

      <Card className="p-5 mb-6" data-testid="erp-global-search">
        <div className="overline mb-1">Buscar en todos los reportes (IA)</div>
        <p className="text-sm text-muted-foreground mb-3">Encuentra todos los pagos de un proveedor (o cualquier concepto) en TODOS los reportes subidos, desde el primero hasta el más reciente.</p>
        <div className="flex flex-wrap gap-2">
          <input data-testid="erp-global-query" className={inputClass + " flex-1 min-w-[220px]"} placeholder="Ej. CEMEX, Aceros del Norte, anticipo…" value={globalQuery} onChange={(e) => setGlobalQuery(e.target.value)} onKeyDown={(e) => e.key === "Enter" && globalSearch()} />
          <PrimaryButton onClick={globalSearch} disabled={globalBusy} data-testid="erp-global-search-btn">{globalBusy ? "Buscando…" : "Buscar con IA"}</PrimaryButton>
        </div>
        {globalResult && (
          <div className="mt-4" data-testid="erp-global-result">
            {globalResult.count === 0 ? (
              <div className="text-sm text-muted-foreground">Sin resultados para “{globalResult.query}” en {globalResult.reports_scanned} reporte(s).</div>
            ) : (
              <>
                <div className="flex flex-wrap gap-4 text-sm mb-3 items-center">
                  <span><b className="text-primary text-lg">{money(globalResult.total)}</b> total</span>
                  <span className="text-muted-foreground">{globalResult.count} pago(s)</span>
                  <span className="text-muted-foreground">{globalResult.first_date} → {globalResult.last_date}</span>
                  <span className="text-muted-foreground">{globalResult.reports_scanned} reporte(s)</span>
                  <button onClick={globalExcel} data-testid="erp-global-excel" className="ml-auto inline-flex items-center gap-1 text-xs font-semibold text-primary hover:underline"><FileXls size={14} weight="fill" /> Exportar Excel</button>
                </div>
                {globalResult.matched_names?.length > 0 && <div className="text-xs text-muted-foreground mb-2">Coincidencias: {globalResult.matched_names.join(", ")}</div>}
                <div className="overflow-x-auto border border-border rounded-sm max-h-96 overflow-y-auto">
                  <table className="w-full text-sm">
                    <thead><tr className="border-b border-border text-left">{["Fecha", "Beneficiario", "Concepto", "Reporte", "Importe"].map((h) => <th key={h} className="px-3 py-2 overline">{h}</th>)}</tr></thead>
                    <tbody>
                      {globalResult.payments.map((r, i) => (
                        <tr key={i} className="border-b border-border last:border-0">
                          <td className="px-3 py-2 whitespace-nowrap">{r.date}</td>
                          <td className="px-3 py-2">{r.beneficiary || "—"}</td>
                          <td className="px-3 py-2">{r.description || "—"}</td>
                          <td className="px-3 py-2 text-muted-foreground">{r.report_name}</td>
                          <td className="px-3 py-2 font-semibold text-right whitespace-nowrap">{money(r.amount)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </div>
        )}
      </Card>

      <Card className="p-5 mb-6" data-testid="erp-summary">
        <div className="overline mb-1">Resumen financiero (todos los reportes)</div>
        <p className="text-sm text-muted-foreground mb-3">Totales de lo gastado y lo cobrado, agrupados como quieras y filtrables por fecha o proveedor/cliente.</p>
        <div className="flex flex-wrap gap-2 items-end">
          <div>
            <label className="text-xs font-semibold text-muted-foreground block mb-1">Agrupar por</label>
            <select data-testid="sum-groupby" value={sumGroupBy} onChange={(e) => setSumGroupBy(e.target.value)} className="h-11 px-3 rounded-sm border border-input bg-white text-sm">
              <option value="proveedor">Proveedor / Cliente</option>
              <option value="semana">Semana</option>
              <option value="mes">Mes</option>
              <option value="anio">Año</option>
              <option value="dia">Día</option>
            </select>
          </div>
          <div><label className="text-xs font-semibold text-muted-foreground block mb-1">Desde</label><input type="date" data-testid="sum-start" value={sumStart} onChange={(e) => setSumStart(e.target.value)} className="h-11 px-3 rounded-sm border border-input bg-white text-sm" /></div>
          <div><label className="text-xs font-semibold text-muted-foreground block mb-1">Hasta</label><input type="date" data-testid="sum-end" value={sumEnd} onChange={(e) => setSumEnd(e.target.value)} className="h-11 px-3 rounded-sm border border-input bg-white text-sm" /></div>
          <input data-testid="sum-q" className={inputClass + " flex-1 min-w-[160px]"} placeholder="Filtrar por proveedor/cliente (opcional)" value={sumQ} onChange={(e) => setSumQ(e.target.value)} />
          <PrimaryButton onClick={loadSummary} disabled={sumBusy} data-testid="sum-btn">{sumBusy ? "Calculando…" : "Calcular"}</PrimaryButton>
        </div>
        {sumData && (
          <div className="mt-4" data-testid="sum-result">
            <div className="grid grid-cols-3 gap-3 mb-4">
              <div className="rounded-sm border border-red-200 bg-red-50 p-3"><div className="overline">Total gastado</div><div className="font-heading font-extrabold text-lg text-red-600">{money(sumData.total_gasto)}</div></div>
              <div className="rounded-sm border border-emerald-200 bg-emerald-50 p-3"><div className="overline">Total cobrado</div><div className="font-heading font-extrabold text-lg text-emerald-600">{money(sumData.total_cobro)}</div></div>
              <div className="rounded-sm border border-border p-3"><div className="overline">Neto (gasto − cobro)</div><div className="font-heading font-extrabold text-lg">{money(sumData.neto)}</div></div>
            </div>
            <div className="overflow-x-auto border border-border rounded-sm max-h-96 overflow-y-auto">
              <table className="w-full text-sm">
                <thead><tr className="border-b border-border text-left">{[sumGroupBy === "proveedor" ? "Proveedor / Cliente" : "Periodo", "Gastado", "Cobrado", "Neto", "# Mov."].map((h) => <th key={h} className="px-3 py-2 overline">{h}</th>)}</tr></thead>
                <tbody>
                  {sumData.groups.length === 0 ? <tr><td colSpan={5} className="px-3 py-4 text-center text-muted-foreground">Sin datos.</td></tr> : sumData.groups.map((g, i) => (
                    <tr key={i} className="border-b border-border last:border-0">
                      <td className="px-3 py-2">{g.key}</td>
                      <td className="px-3 py-2 text-red-600 font-semibold">{money(g.gasto)}</td>
                      <td className="px-3 py-2 text-emerald-600 font-semibold">{money(g.cobro)}</td>
                      <td className="px-3 py-2 font-semibold">{money(g.neto)}</td>
                      <td className="px-3 py-2 text-muted-foreground">{g.count}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </Card>

      {reports.length === 0 ? (
        <EmptyState icon={Table} title="Sin reportes" subtitle="Sube el reporte de pagos de tu ERP (Excel, PDF o CSV). La IA lo digiere y lo desglosa por fechas; luego lo exportas a Excel por periodos." />
      ) : (
        <div className="space-y-2">
          {reports.map((r) => (
            <Card key={r.id} data-testid={`erp-report-${r.id}`} className="p-4 flex items-center gap-3">
              <div className={`p-2 rounded-sm ${r.status === "listo" ? "bg-emerald-50 text-emerald-600" : r.status === "error" ? "bg-red-50 text-red-600" : "bg-amber-50 text-amber-600"}`}><Table size={20} weight="fill" /></div>
              <button onClick={() => r.status === "listo" && openDetail(r)} className="flex-1 min-w-0 text-left">
                <div className="font-semibold truncate">{r.name}</div>
                <div className="text-xs text-muted-foreground mt-0.5">
                  {r.status === "procesando" ? <span className="inline-flex items-center gap-1 text-amber-600"><ArrowClockwise size={12} className="animate-spin" /> Procesando con IA…</span>
                    : r.status === "error" ? <span className="text-red-600">Error: {r.error}</span>
                      : `${r.row_count} movimientos · Total ${money(r.total)}`}
                </div>
              </button>
              {r.status === "listo" && <button onClick={() => openDetail(r)} data-testid={`erp-open-${r.id}`} className="text-sm text-primary hover:underline">Ver desglose</button>}
              {r.file_path && <a href={fileUrl(r.file_path)} target="_blank" rel="noreferrer" data-testid={`erp-download-original-${r.id}`} className="inline-flex items-center gap-1 text-sm text-primary hover:underline"><FileXls size={14} weight="fill" /> Descargar</a>}
              {canDelete && <button onClick={() => remove(r.id)} data-testid={`erp-delete-${r.id}`} className="text-muted-foreground hover:text-danger"><Trash size={16} /></button>}
            </Card>
          ))}
        </div>
      )}

      <Modal open={open} onClose={() => setOpen(false)} title="Subir reporte del ERP">
        <form onSubmit={save} className="space-y-4">
          <div className="text-sm text-muted-foreground">Acepta <b className="text-foreground">Excel (.xlsx/.xls), PDF o CSV</b>. La IA leerá el archivo, normalizará fechas e importes y lo desglosará por periodos.</div>
          <Field label="Nombre del reporte (opcional)"><input data-testid="erp-name" className={inputClass} placeholder="Se guardará como 'Reporte del <fecha>' si lo dejas vacío" value={form.name} onChange={(e) => setForm({ name: e.target.value })} /></Field>
          <Field label="Archivo del reporte *">
            <input data-testid="erp-file" type="file" accept=".xlsx,.xls,.csv,.pdf" onChange={(e) => setFile(e.target.files[0])}
              className="w-full text-sm border border-dashed border-input rounded-sm p-3 file:mr-3 file:px-3 file:py-1.5 file:border-0 file:bg-primary file:text-white file:rounded-sm file:font-medium" />
          </Field>
          <PrimaryButton type="submit" disabled={busy} data-testid="erp-save-btn">{busy ? "Subiendo…" : "Subir y digerir"}</PrimaryButton>
        </form>
      </Modal>
    </div>
  );
}
